/**
 * `Collapsible` — a lightweight disclosure. A labelled header row toggles
 * a body open/closed; collapsed by default. Introduced 2026-07-14 to tuck
 * the long per-platform install instructions behind an "Instructions"
 * toggle (founder: "too much text now") without losing them.
 *
 * Deliberately minimal: no animation dependency, no portal — just a
 * button + conditional body, theme-aware via `useC`. Keyboard- and
 * screen-reader-friendly (`aria-expanded` on a real <button>).
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export interface CollapsibleProps {
  /** Header label, e.g. "Instructions". */
  label: string;
  /** Start expanded. Defaults to collapsed. */
  defaultOpen?: boolean;
  /**
   * `boxed` (default) — a bordered card with a full-width header.
   * `inline` — a subtle muted text-link trigger (no border, no
   * background) that blends into the surrounding title row, expanding
   * borderless prose (founder 2026-07-15: the boxed install instructions
   * "take a lot of space… should be subtle, maybe blend into the title").
   */
  variant?: 'boxed' | 'inline';
  children: ReactNode;
}

export function Collapsible({
  label,
  defaultOpen = false,
  variant = 'boxed',
  children,
}: CollapsibleProps): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(defaultOpen);

  if (variant === 'inline') {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="inline-flex items-center gap-1"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          {label}
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        {open ? <div style={{ marginTop: 10 }}>{children}</div> : null}
      </>
    );
  }

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 3 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center justify-between w-full px-4 py-3"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 500,
          color: C.text,
          textAlign: 'left',
        }}
      >
        <span>{label}</span>
        {open ? (
          <ChevronDown size={15} color={C.muted} />
        ) : (
          <ChevronRight size={15} color={C.muted} />
        )}
      </button>
      {open ? (
        <div className="px-4 pb-4" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
          <div style={{ paddingTop: 12 }}>{children}</div>
        </div>
      ) : null}
    </div>
  );
}
